import { Button, PopoverProps } from '@mantine/core';
import { useLocation, useSearch } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ColorType } from '../../../types/ColorPicker';
import { extractThemeColors } from '../../../utils/extractThemeColors';
import { searchObjectToParams } from '../../../utils/query.utils';
import { useGlobalTheme } from '../../../utils/useGlobalTheme';
import { useWorkMode } from '../../../utils/useWorkMode';
import { ColorPickerPopover } from '../../pickers/ColorPickerPopover';
import { useLearningObjectCommonData } from '../../providers/LearningObjectCommonDataProvider';

type ThemeColorPickerProps = {
  color: string;
  onSelectColor: ({ color, type }: { color: string; type: ColorType }) => void;
  withGradient?: boolean;
  renderPopoverTarget: ({
    onClick,
    onSelectColor,
    color,
    selected
  }: {
    onClick: () => void;
    onSelectColor: (color: string) => void;
    color: string;
    selected: boolean;
  }) => ReactNode;
} & PopoverProps;

export function ThemeColorPicker(props: ThemeColorPickerProps) {
  const { color, onSelectColor, withGradient = false, renderPopoverTarget, withinPortal } = props;

  const theme = useGlobalTheme();
  const themeColors = extractThemeColors({ colors: theme.other.colors });

  const EditThemeColors = () => {
    const { pathname } = useLocation();
    const search = useSearch({ strict: false });
    const { isStudioEdit } = useWorkMode();
    const { t } = useTranslation('components.colorPicker');
    const { themeId } = useLearningObjectCommonData();

    const handleEdit = () => {
      if (themeId) {
        const params = search ? searchObjectToParams(search) : new URLSearchParams();
        params.set('themeId', themeId);
        window.history.pushState(null, '', `${pathname}?${params.toString()}`);
      }
    };

    if (!isStudioEdit || !themeId) {
      return null;
    }
    return (
      <Button variant="transparent" onClick={handleEdit}>
        {t('edit')}
      </Button>
    );
  };

  return (
    <ColorPickerPopover
      themeColors={themeColors}
      color={color}
      onSelectColor={onSelectColor}
      renderPopoverTarget={renderPopoverTarget}
      withGradient={withGradient}
      withinPortal={withinPortal}
      editComponent={<EditThemeColors />}
    />
  );
}
